import { extraServiceDetails } from "./service-details-extra";

export type ServiceDetail = {
  slug: string;
  name: string;
  eyebrow: string;
  title: string;
  intro: string;
  metaTitle: string;
  metaDescription: string;
  /** What the client receives, grouped for scanability. */
  deliverables: { group: string; items: string[] }[];
  /** Operating cadence — what happens, and when. */
  timeline: { when: string; label: string; detail: string }[];
  /** Onboarding runway before steady state. */
  onboarding: { step: string; duration: string; detail: string }[];
  outcomes: string[];
  tools: string[];
};

const baseServiceDetails: ServiceDetail[] = [
  {
    slug: "bookkeeping",
    name: "Bookkeeping",
    eyebrow: "Starting at $199/mo",
    title: "Professional US Bookkeeping from $199/month.",
    intro:
      "Built specifically for businesses that don't need a full-time in-house bookkeeper. Monthly bank & card reconciliations, transaction categorization, and CPA-ready financial statements in QuickBooks Online or Xero — saving you over $60,000/year.",
    metaTitle: "US Bookkeeping Services from $199/mo | Finendra",
    metaDescription:
      "Fractional US bookkeeping starting at $199/mo for businesses that don't need a full-time bookkeeper. Daily transaction recording, monthly bank feed reconciliations, and CPA tax-ready reporting.",
    deliverables: [
      {
        group: "Monthly & recurring",
        items: [
          "Bank, credit card, and merchant feed reconciliations (up to 75 txns on Essential)",
          "Consistent categorization against your chart of accounts",
          "Receipt and document capture with source-document attachment",
          "Monthly Profit & Loss (P&L) and Balance Sheet reporting",
          "Year-end 1099 vendor tracking & CPA-ready workpaper tax packet",
        ],
      },
      {
        group: "Dual-layer oversight",
        items: [
          "Named dedicated bookkeeper assigned directly to your company",
          "Senior Controller quality review on every month-end close",
          "Shared questions log with 24–48hr turnaround",
          "Audit-ready documentation trail for every entry",
        ],
      },
      {
        group: "Fractional advantage",
        items: [
          "Save over $60,000/year compared to a $65k/yr full-time in-house hire",
          "Month-to-month retainers with zero long-term lock-in",
          "Seamless upgrade path to Growth ($499) and Scale ($1,299) as volume expands",
        ],
      },
    ],
    timeline: [
      {
        when: "Daily / Weekly",
        label: "Transactions recorded",
        detail: "New bank and card activity is coded within 24–48 hours of feed sync.",
      },
      {
        when: "Weekly",
        label: "Categorization review",
        detail: "Uncategorized items cleared and client queries sent in a single digest.",
      },
      {
        when: "Business day 5–10",
        label: "Reconciliations complete",
        detail: "All accounts reconciled for the prior month with variances documented.",
      },
      {
        when: "Month-end",
        label: "Financials delivered",
        detail: "P&L and Balance Sheet finalized and sent to you or directly to your CPA.",
      },
    ],
    onboarding: [
      {
        step: "Discovery & access",
        duration: "Days 1–2",
        detail:
          "Connect read-only access to QuickBooks Online or Xero and verify your bank feeds.",
      },
      {
        step: "Chart of accounts review",
        duration: "Days 2–4",
        detail: "We tune and organize your account structure for clean, tax-optimized categorization.",
      },
      {
        step: "Catch-up (if needed)",
        duration: "1–2 weeks",
        detail: "Historical neglected months rebuilt and reconciled before live steady-state.",
      },
      {
        step: "Steady state",
        duration: "Ongoing",
        detail: "Monthly reconciliations, P&L reporting, and ongoing 1099 tracking run on autopilot.",
      },
    ],
    outcomes: [
      "Save $60,000+ per year vs hiring an in-house full-time bookkeeper",
      "Books that are always 100% current and reconciled to the bank feed",
      "A spotless starting point for tax season with CPA-ready workpapers",
    ],
    tools: ["QuickBooks Online", "Xero", "Mercury", "Brex", "Ramp", "Chase", "Stripe"],
  },
  {
    slug: "payroll",
    name: "Payroll Processing",
    eyebrow: "Service",
    title: "Full-cycle payroll, filed accurately and on time.",
    intro:
      "Multi-state and global payroll, benefit deductions, contractor payments, and statutory filings handled end to end — with a cutoff calendar your team can plan around.",
    metaTitle: "Outsourced Payroll Processing Services | Finendra",
    metaDescription:
      "Multi-state and global payroll runs, benefit deductions, international contractor payments, and tax filings with a fixed cutoff and approval calendar.",
    deliverables: [
      {
        group: "Each pay cycle",
        items: [
          "Timesheet and hours intake with exception flagging",
          "Gross-to-net calculation including overtime and bonuses",
          "Benefit, retirement, and garnishment deductions applied",
          "Pre-run register sent for your approval before funding",
        ],
      },
      {
        group: "Compliance & filings",
        items: [
          "Federal, state, and international payroll tax deposits",
          "Statutory filings and quarterly compliance coordinated with your provider",
          "Multi-jurisdiction registration tracking and global contractor compliance",
          "Year-end W-2, 1099-NEC, and regional tax form distribution",
        ],
      },
      {
        group: "Accounting integration",
        items: [
          "Payroll journal entries posted to the general ledger",
          "Accrued wages and PTO liability tracked monthly",
          "Departmental or class-level labor cost allocation",
        ],
      },
    ],
    timeline: [
      {
        when: "T–4 days",
        label: "Hours intake closes",
        detail: "Timesheets, commissions, and one-off adjustments collected.",
      },
      {
        when: "T–3 days",
        label: "Draft register issued",
        detail: "You receive the pre-run register with exceptions highlighted.",
      },
      {
        when: "T–2 days",
        label: "Approval & funding",
        detail: "Approved register is submitted and funding requirements confirmed.",
      },
      {
        when: "Pay date",
        label: "Payroll lands",
        detail: "Direct deposits settle and pay stubs are available to employees.",
      },
      {
        when: "T+1 day",
        label: "Posted to the ledger",
        detail: "Payroll journal entries and accruals recorded in the books.",
      },
    ],
    onboarding: [
      {
        step: "Provider & data audit",
        duration: "Days 1–5",
        detail:
          "Review of your payroll platform, tax jurisdictions and states, pay groups, and current deductions.",
      },
      {
        step: "Parallel run",
        duration: "1 cycle",
        detail:
          "We shadow one live cycle and reconcile our output against yours before switching over.",
      },
      {
        step: "Cutover",
        duration: "Next cycle",
        detail: "Finendra runs payroll end to end on the published calendar.",
      },
    ],
    outcomes: [
      "No missed filings or late deposits",
      "Predictable approval windows for your managers",
      "Labor cost visible in the ledger the day after payday",
    ],
    tools: ["Gusto", "ADP", "Paychex", "Rippling", "Justworks", "QuickBooks Payroll"],
  },
  {
    slug: "accounts-payable",
    name: "Accounts Payable (AP)",
    eyebrow: "Service",
    title: "Every bill captured, approved, and paid on schedule.",
    intro:
      "A controlled AP workflow: vendor bills enter one inbox, route through your approval rules, and get paid in batches you sign off on — with no duplicate or late payments.",
    metaTitle: "Outsourced Accounts Payable (AP) Services | Finendra",
    metaDescription:
      "Bill capture, three-way matching, approval routing, and scheduled payment runs with weekly AP aging and cash requirement reporting.",
    deliverables: [
      {
        group: "Bill processing",
        items: [
          "Dedicated AP inbox with automated bill capture and coding",
          "Duplicate detection before anything enters the approval queue",
          "Purchase order and receipt matching where you use POs",
          "Vendor master data maintenance and W-9 collection",
        ],
      },
      {
        group: "Approvals & payments",
        items: [
          "Approval routing that mirrors your internal authority limits",
          "Scheduled payment runs by ACH, check, or virtual card",
          "Early-payment discount capture where terms allow",
          "Payment confirmations filed against each bill",
        ],
      },
      {
        group: "Reporting",
        items: [
          "Weekly AP aging with upcoming cash requirements",
          "Vendor spend summary by category",
          "Month-end accrual of unbilled and unpaid obligations",
        ],
      },
    ],
    timeline: [
      {
        when: "Daily",
        label: "Bills captured & coded",
        detail: "Invoices received in the AP inbox are entered within one business day.",
      },
      {
        when: "Mondays",
        label: "Approval queue sent",
        detail: "Approvers receive the batch awaiting sign-off with due dates flagged.",
      },
      {
        when: "Wednesdays",
        label: "Payment run",
        detail: "Approved bills are paid in a single scheduled run after cash confirmation.",
      },
      {
        when: "Fridays",
        label: "AP aging delivered",
        detail: "Current aging plus next week's cash requirement sent to you.",
      },
      {
        when: "Business day 3",
        label: "Month-end accruals",
        detail: "Unpaid and unbilled obligations accrued for the close.",
      },
    ],
    onboarding: [
      {
        step: "Vendor & terms review",
        duration: "Days 1–5",
        detail: "We map your vendor list, payment terms, and current approval chain.",
      },
      {
        step: "Workflow build",
        duration: "Days 5–10",
        detail: "AP inbox, coding rules, and approval limits configured and tested.",
      },
      {
        step: "First controlled run",
        duration: "Week 3",
        detail: "First payment run executed with you approving every item.",
      },
    ],
    outcomes: [
      "No late fees or duplicate payments",
      "Approvals that take minutes, not email threads",
      "Cash requirements known a week in advance",
    ],
    tools: ["Bill.com", "Ramp", "Melio", "Stampli", "QuickBooks Online", "Xero"],
  },
  {
    slug: "accounts-receivable",
    name: "Accounts Receivable (AR)",
    eyebrow: "Service",
    title: "Invoices out fast, cash in sooner.",
    intro:
      "Accurate invoicing, disciplined collections follow-up, and aging control that shortens your cash conversion cycle without straining customer relationships.",
    metaTitle: "Outsourced Accounts Receivable (AR) Services | Finendra",
    metaDescription:
      "Invoicing, payment application, dunning cadence, and AR aging management designed to shorten days sales outstanding.",
    deliverables: [
      {
        group: "Invoicing",
        items: [
          "Invoice generation from contracts, milestones, or usage data",
          "Recurring and subscription billing schedules maintained",
          "Customer master data, terms, and credit limits kept current",
          "Sales tax applied per jurisdiction where required",
        ],
      },
      {
        group: "Collections",
        items: [
          "Structured dunning cadence with branded reminder templates",
          "Escalation list of accounts needing your direct involvement",
          "Payment plan tracking for negotiated balances",
          "Dispute log with resolution status by customer",
        ],
      },
      {
        group: "Cash application & reporting",
        items: [
          "Daily payment application against open invoices",
          "Weekly AR aging with DSO trend",
          "Bad-debt and allowance recommendations at month end",
        ],
      },
    ],
    timeline: [
      {
        when: "Daily",
        label: "Cash applied",
        detail: "Incoming payments matched to invoices and posted the same business day.",
      },
      {
        when: "Invoice date",
        label: "Invoices issued",
        detail: "Invoices go out on the agreed billing calendar, not in a month-end batch.",
      },
      {
        when: "Day 7 / 14 / 30 past due",
        label: "Dunning cadence",
        detail: "Automated reminders escalate in tone on a fixed schedule.",
      },
      {
        when: "Day 45+",
        label: "Escalation review",
        detail: "Aged accounts handed to you with full contact history for a decision.",
      },
      {
        when: "Weekly",
        label: "AR aging delivered",
        detail: "Aging report and DSO trend sent with the collections summary.",
      },
    ],
    onboarding: [
      {
        step: "Billing model review",
        duration: "Days 1–5",
        detail: "We document how you bill, your terms, and where invoices currently stall.",
      },
      {
        step: "Template & cadence setup",
        duration: "Days 5–10",
        detail: "Invoice templates, reminder copy, and escalation rules approved by you.",
      },
      {
        step: "Aging cleanup",
        duration: "2–4 weeks",
        detail: "Existing aged balances worked through before steady-state collections begin.",
      },
    ],
    outcomes: [
      "Lower days sales outstanding",
      "Fewer awkward collections calls for your team",
      "A receivables list you can actually trust",
    ],
    tools: ["QuickBooks Online", "Xero", "Stripe", "Bill.com", "Chaser", "HubSpot"],
  },
  {
    slug: "month-end-close",
    name: "Month-End Closing",
    eyebrow: "Service",
    title: "A disciplined close, finished in days — not weeks.",
    intro:
      "A documented close calendar, a checklist owned by a named reviewer, and finished financials with CPA-ready workpapers by the fifth business day.",
    metaTitle: "Month-End Close Services | 5-Day Close Calendar | Finendra",
    metaDescription:
      "Reconciliations, accruals, review, and financial statements delivered on a documented month-end close calendar with CPA-ready workpapers.",
    deliverables: [
      {
        group: "Close execution",
        items: [
          "Documented close checklist with an owner for every task",
          "All bank, card, and balance sheet accounts reconciled",
          "Accruals, prepaids, deferrals, and depreciation entries posted",
          "Intercompany and inventory adjustments where applicable",
        ],
      },
      {
        group: "Review & controls",
        items: [
          "Second-level review by a senior accountant before release",
          "Flux analysis on material month-over-month movements",
          "Balance sheet substantiation with supporting schedules",
          "Open-items list carried forward with due dates",
        ],
      },
      {
        group: "Reporting package",
        items: [
          "P&L, balance sheet, and cash flow statement",
          "Budget vs. actual with variance commentary",
          "KPI summary tailored to your business model",
          "CPA-ready workpaper folder for tax and audit",
        ],
      },
    ],
    timeline: [
      {
        when: "Business day 1",
        label: "Cutoff & data pull",
        detail: "Period locked for new entries; feeds, payroll, and AP/AR data pulled.",
      },
      {
        when: "Business day 2",
        label: "Reconciliations",
        detail: "All cash, card, and balance sheet accounts reconciled and evidenced.",
      },
      {
        when: "Business day 3",
        label: "Adjusting entries",
        detail: "Accruals, prepaids, deferrals, and depreciation posted.",
      },
      {
        when: "Business day 4",
        label: "Senior review",
        detail: "Flux analysis and second-level review; open items resolved or logged.",
      },
      {
        when: "Business day 5",
        label: "Financials delivered",
        detail: "Reporting package and workpapers issued; review call if scheduled.",
      },
    ],
    onboarding: [
      {
        step: "Close diagnostic",
        duration: "Days 1–5",
        detail: "We time your current close and identify what is causing the delay.",
      },
      {
        step: "Calendar & checklist build",
        duration: "Days 5–10",
        detail: "A close calendar with named owners and deadlines agreed with your team.",
      },
      {
        step: "First supervised close",
        duration: "Month 1",
        detail: "We run the close alongside your team, typically landing in 7–8 business days.",
      },
      {
        step: "5-day close",
        duration: "From month 2",
        detail: "Steady-state close delivered by the fifth business day.",
      },
    ],
    outcomes: [
      "Financials you can act on in the first week of the month",
      "No scramble at year end or during diligence",
      "A close process that survives staff turnover",
    ],
    tools: ["QuickBooks Online", "Xero", "Fathom", "Google Sheets", "Notion", "Slack"],
  },
];

export const serviceDetails: ServiceDetail[] = [...baseServiceDetails, ...extraServiceDetails];

export function getServiceDetail(slug: string) {
  return serviceDetails.find((s) => s.slug === slug);
}
